import { useEffect } from "react";

/**
 * When Launchpad is embedded via iframe in a merchant's Shopify theme,
 * the parent window needs to know the rendered height to size the iframe.
 * We post the height every time layout changes.
 *
 * Also: when we do a full-page redirect (e.g. → Shopify checkout URL),
 * we want it to happen on the PARENT window, not inside the iframe.
 * That's handled in the pre-order submit via `window.top.location`.
 */
export default function useEmbedResize() {
  useEffect(() => {
    if (typeof window === "undefined") return;
    const inIframe = window.self !== window.top;
    if (!inIframe) return;

    let lastHeight = 0;
    const send = () => {
      const h = document.documentElement.scrollHeight;
      if (h !== lastHeight) {
        lastHeight = h;
        window.parent.postMessage({ type: "launchpad:height", height: h }, "*");
      }
    };

    // Initial + on any DOM mutation
    send();
    const ro = new ResizeObserver(send);
    ro.observe(document.documentElement);
    const mo = new MutationObserver(send);
    mo.observe(document.body, { subtree: true, childList: true, characterData: true });
    // Poll periodically as a fallback for tricky cases (image loads, async fetch)
    const t = setInterval(send, 1500);
    return () => {
      ro.disconnect();
      mo.disconnect();
      clearInterval(t);
    };
  }, []);
}

export function isEmbedded() {
  if (typeof window === "undefined") return false;
  try {
    return window.self !== window.top;
  } catch {
    return true; // cross-origin block → we are embedded
  }
}
